// The printable per-line summary (PR-E; O154 F7: nothing is sent to the facility in v1 —
// staff print this and fax or hand it back). One row per line of THIS sheet: resident,
// med, outcome (drafted / needs review / not drafted + why). Nothing about any other
// resident, and no Rx# (the pharmacy's number stays on the draft, F6).
//
// Printing: the view is rendered into document.body beside the app and the page's print
// stylesheet (styles/refill-sheet.css) shows ONLY it while <html> carries
// `rs-printing` — no new window (a popup blocker, and the app's CSP), no inline <style>.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { printRows, printTitle, summaryCounts } from '../../lib/refill-sheet.js';

export function SheetPrintView({ job, printedAt = null }) {
  const rows = printRows(job);
  const counts = summaryCounts(job);
  const read = job && job.createdAt ? new Date(job.createdAt) : null;
  const when = printedAt || new Date();
  return (
    <div className="rs-print" data-role="refill-sheet-print">
      <h1>{printTitle(job)}</h1>
      <div className="rs-print-meta">
        {read && <span>Sheet received {read.toLocaleDateString()} </span>}
        <span>· Printed {when.toLocaleDateString()} </span>
        <span>· {counts.drafted} drafted, {counts.needsAttention} need review, {counts.notDrafted} not drafted</span>
      </div>
      <table>
        <thead>
          <tr><th scope="col">Line</th><th scope="col">Resident</th><th scope="col">Medication</th><th scope="col">Outcome</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.line}>
              <td>{r.line}</td>
              <td>{r.resident}</td>
              <td>{r.med}</td>
              <td>{r.outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="rs-print-foot">
        Drafted: an unsigned refill is waiting for the provider's signature in Elation — nothing
        reaches the pharmacy until it is signed. Lines that need review or were not drafted have
        no refill yet.
      </p>
    </div>
  );
}

// Mounts the print view, prints, and unmounts it when the print dialog closes
// (`afterprint`, which every supported browser fires). Until then it stays mounted: on
// screen it is display:none, and a browser whose print() returns before the dialog
// closes still has the summary to print.
export function SheetPrintPortal({ job, onDone }) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('rs-printing');
    const done = () => {
      root.classList.remove('rs-printing');
      window.removeEventListener('afterprint', done);
      if (onDone) onDone();
    };
    window.addEventListener('afterprint', done);
    // Let the portal paint before the dialog snapshots the page.
    const t = setTimeout(() => window.print(), 50);
    return () => { clearTimeout(t); root.classList.remove('rs-printing'); window.removeEventListener('afterprint', done); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(<div className="rs-print-root"><SheetPrintView job={job} /></div>, document.body);
}
